import { useCallback } from 'react';
import { Task, FullProject, LogEntry } from '../../../types';
import { isInboxProject, isCommProject, areProjectIdsMatching, isMatchingTaskId } from '../../../utils/idNormalizer';
import { isGlobalDragBlocked } from '../../../utils/dragGuardState';
import { ensureFractionalSortOrder } from '../taskPositioningUtils';
import { ProjectSyncTracker } from '../sync/projectSyncStateTracker';

export interface UseTaskAddActionProps {
  currentProjectId: string;
  tracker: ProjectSyncTracker;
  setAllProjects: (updater: FullProject[] | ((prev: FullProject[]) => FullProject[])) => void;
  setTasks: React.Dispatch<React.SetStateAction<Task[]>>;
  markProjectDirty: (id: string) => void;
  onMarkTaskDirty: (taskId: string | number, projectId?: string) => void;
  addLogEntry: (action: LogEntry['action'], description: string, details?: string) => void;
  user: { uid?: string; email?: string | null; displayName?: string | null } | null;
  tenantId: string;
  COMM_PROJECT_ID: string;
  INBOX_PROJECT_ID: string;
  normalizeProjectId: (id?: string) => string | undefined;
}

export function useTaskAddAction({
  currentProjectId,
  tracker,
  setAllProjects,
  setTasks,
  markProjectDirty,
  onMarkTaskDirty,
  addLogEntry,
  user,
  tenantId,
  COMM_PROJECT_ID,
  INBOX_PROJECT_ID,
  normalizeProjectId,
}: UseTaskAddActionProps) {
  const addTask = useCallback(
    (
      type: Task['type'] = 'task',
      parentId?: string | number,
      projectId?: string,
      name?: string,
      initialData: Partial<Task> = {},
      options?: { bypassDragGuard?: boolean }
    ) => {
      let rawProjectId = projectId || currentProjectId;
      if (!rawProjectId || rawProjectId === 'global' || rawProjectId === 'globalKanban' || rawProjectId === 'communication') {
        rawProjectId = type === 'contact' ? 'comm_internal' : 'inbox_inforum';
      }
      const isComm = isCommProject(rawProjectId) || (!rawProjectId && type === 'contact');
      const targetProjectId = isComm ? COMM_PROJECT_ID : isInboxProject(rawProjectId) ? INBOX_PROJECT_ID : rawProjectId;
      const normalizedTargetId = normalizeProjectId(targetProjectId);
      const currentAllProjects = tracker.getAllProjects();
      let project = currentAllProjects.find(p => areProjectIdsMatching(p.id, normalizedTargetId));
      if (!project) {
        project = currentAllProjects.find(p => areProjectIdsMatching(p.id, type === 'contact' ? COMM_PROJECT_ID : INBOX_PROJECT_ID));
      }
      if (!project) return;
      const actualTargetId = project.id;

      if (!options?.bypassDragGuard && isGlobalDragBlocked(600)) {
        return;
      }

      const parentTask = parentId
        ? project.tasks.find(t => t.id.toString() === parentId.toString())
        : null;
      const defaultStart = parentTask?.start || '';
      const defaultEnd = parentTask?.end || '';

      const rawSortOrder =
        initialData.sortOrder ??
        (project.tasks.length > 0
          ? Math.max(...project.tasks.map(t => t.sortOrder || 0)) + 1000
          : 1000);
      const nextSortOrder = ensureFractionalSortOrder(rawSortOrder, actualTargetId);

      const initialStatus = initialData.status || (type === 'contact' ? 'pool' : 'open');
      const customCol = project.metadata?.customColumns?.find(c => c.id === initialStatus);
      const isCompleted = initialStatus === 'completed' || customCol?.category === 'completed';
      const isRealTargetProject = Boolean(
        actualTargetId && !isCommProject(actualTargetId) && !isInboxProject(actualTargetId)
      );
      const mergedProjectIds = isRealTargetProject
        ? Array.from(new Set([...(initialData.projectIds || []), actualTargetId]))
        : initialData.projectIds;

      const newTask: Task = {
        id: Date.now().toString() + Math.random().toString(36).substring(2, 5),
        createdAt: Date.now(),
        updatedAt: Date.now(),
        name: name !== undefined ? name : '',
        start: defaultStart,
        end: defaultEnd,
        type,
        completed: initialData.completed !== undefined ? initialData.completed : isCompleted,
        status: initialStatus,
        priority: 'none',
        assignee: '',
        color: 'bg-blue-500',
        startTime: '09:00',
        endTime: '10:00',
        parentId,
        isInlineNew: name !== undefined && name.trim() !== '' ? false : true,
        userId: user?.uid,
        tenantId: project.tenantId || tenantId,
        history: [
          {
            id: Math.random().toString(36).substr(2, 9),
            timestamp: Date.now(),
            user: user?.email || 'System',
            field: 'create',
            message: 'Aufgabe erstellt',
          },
        ],
        ...initialData,
        sortOrder: nextSortOrder,
        projectId: actualTargetId,
        ...(mergedProjectIds ? { projectIds: mergedProjectIds } : {}),
      };

      const isCurrentActive = areProjectIdsMatching(actualTargetId, currentProjectId);

      const updatedProjects = currentAllProjects.map(p => {
        if (
          areProjectIdsMatching(p.id, actualTargetId) ||
          (isCurrentActive && areProjectIdsMatching(p.id, currentProjectId))
        ) {
          const existingTasks = (p.tasks || []).filter(t => !isMatchingTaskId(t.id, newTask.id));
          return { ...p, tasks: [...existingTasks, newTask], lastModified: Date.now() };
        }
        return p;
      });

      tracker.syncMultipleTasks([newTask], true);
      tracker.commitProjects(
        updatedProjects,
        setAllProjects,
        isCurrentActive ? [actualTargetId, currentProjectId] : [actualTargetId]
      );

      if (isCurrentActive) {
        setTasks(prev => [...prev.filter(t => !isMatchingTaskId(t.id, newTask.id)), newTask]);
        markProjectDirty(currentProjectId);
        onMarkTaskDirty(newTask.id, currentProjectId);
        addLogEntry(
          'create',
          `${parentId ? 'Unteraufgabe' : type === 'task' ? 'Aufgabe' : type === 'contact' ? 'Kontakt' : 'Meilenstein'} erstellt`
        );
      } else {
        markProjectDirty(actualTargetId);
        onMarkTaskDirty(newTask.id, actualTargetId);
      }

      return newTask;
    },
    [
      currentProjectId,
      tenantId,
      addLogEntry,
      markProjectDirty,
      onMarkTaskDirty,
      setAllProjects,
      setTasks,
      INBOX_PROJECT_ID,
      COMM_PROJECT_ID,
      user?.email,
      user?.uid,
      normalizeProjectId,
      tracker,
    ]
  );

  return { addTask };
}
